import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Percent } from 'lucide-react';
import { AppShell } from '@/components/layout/AppShell';
import { Card, CardHeader, CardBody } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { EmptyState, Spinner } from '@/components/ui/EmptyState';
import { supabase } from '@/lib/supabaseClient';
import { rxkModuleIcon, rxkModuleTitle, rxkNavGroups } from '../nav';

interface DiscountRow {
  id: string;
  code: string | null;
  label: string;
  type: 'percent' | 'fixed';
  value: number;
  status: string;
  used_count: number;
  max_uses: number | null;
}

export default function AdminDiscounts() {
  const navigate = useNavigate();
  const [discounts, setDiscounts] = useState<DiscountRow[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase
      .from('rxk_discounts')
      .select('id, code, label, type, value, status, used_count, max_uses')
      .order('created_at', { ascending: false });
    setDiscounts((data as DiscountRow[]) ?? []);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <AppShell moduleTitle={rxkModuleTitle} moduleIcon={rxkModuleIcon} navGroups={rxkNavGroups}>
      <div className="space-y-5">
        <div>
          <h1 className="text-[22px] font-bold text-ink">Discounts</h1>
          <p className="text-[12px] text-ink-faint mt-0.5">View only — create or change discounts from ERP.</p>
        </div>

        <Card>
          <CardHeader
            title="Discount Codes"
            subtitle="No code = automatic for everyone. A code = customer must enter it at checkout."
            icon={<Percent className="w-4 h-4" />}
          />
          <CardBody className="space-y-1">
            {loading ? (
              <Spinner />
            ) : discounts.length === 0 ? (
              <EmptyState title="No discounts yet" />
            ) : (
              discounts.map((d) => (
                <button
                  key={d.id}
                  onClick={() => navigate(`/admin/discounts/${d.id}`)}
                  className="w-full flex items-center justify-between gap-3 px-2 py-2 rounded-lg hover:bg-surface-muted transition-colors duration-150 text-left cursor-pointer"
                >
                  <div className="min-w-0">
                    <p className="text-[12.5px] font-semibold text-ink truncate">
                      {d.label} {d.code ? <span className="text-ink-faint font-normal">· {d.code}</span> : <Badge tone="info">Automatic</Badge>}
                    </p>
                    <p className="text-[11px] text-ink-faint">
                      {d.type === 'percent' ? `${d.value}%` : `৳${Number(d.value).toLocaleString()}`} off · used {d.used_count}
                      {d.max_uses ? ` / ${d.max_uses}` : ''} times
                    </p>
                  </div>
                  <Badge tone={d.status === 'active' ? 'success' : 'neutral'}>{d.status}</Badge>
                </button>
              ))
            )}
          </CardBody>
        </Card>
      </div>
    </AppShell>
  );
}
